import React, { useContext } from 'react';
import { sessionState as session } from '../../app/sessionState';
import config from '@/app/config';
import { ErrorReporter } from '@/app/tracking';
import { relativeDateReviver } from '@/utils';

function mapCardUrl(url) {
  try {
    // Create URL object to parse the input
    const parsedUrl = new URL(url);

    // Check if it's a valid adenin.com domain
    if (!parsedUrl.hostname.endsWith('adenin.com')) {
      return url;
    }

    // Extract the path segments
    const pathSegments = parsedUrl.pathname.split('/').filter(segment => segment);

    // Find the card ID (UUID format) in the path
    const cardIdIndex = pathSegments.findIndex(segment =>
      segment === 'card' &&
      pathSegments[pathSegments.indexOf(segment) + 1]?.match(
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      )
    );

    // If no valid card ID is found, return original URL
    if (cardIdIndex === -1 || !pathSegments[cardIdIndex + 1]) {
      return url;
    }

    // Get the card ID
    const cardId = pathSegments[cardIdIndex + 1];

    // Construct the standardized URL
    return `https://${parsedUrl.hostname}/api/gateway/card/${cardId}?channel=artifacts&includeCardDefinition=true`;

  } catch (error) {
    // If URL parsing fails, return the original URL
    return url;
  }
}

export async function fetchData(url, context) {
  console.log("fetchData", url, context);
  let token = session.token;
  if (context?.meta?.token) token = context.meta.token;
  // console.log("fetchData", url, token);

  let isSampleData = url.indexOf("adenin-catalog.netlify.app") > -1;
  url = mapCardUrl(url);

  let options = {
    // credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    }
  };

  // Include cookies and x-subject header when sub has a value
  if (session.sub) {
    options.credentials = 'include';
    options.headers['x-subject'] = session.sub;
  }

  // Add x-at-context header with JSON values when session contains channel or cardId
  const contextData = {};
  if (session.channel) {
    contextData.channel = session.channel;
    // Transform channel value when session.channel has form a.b and session.endpointId has form c:d:e
    if (session.channel.includes('.') && session.endpointId && session.endpointId.split(':').length === 3) {
      const channelParts = session.channel.split('.');
      const endpointParts = session.endpointId.split(':');
      if (channelParts.length === 2 && endpointParts.length === 3) {
        // Format: d.e.b (where d=endpointParts[1], e=endpointParts[2], b=channelParts[1])
        contextData.channel = `${endpointParts[1]}.${endpointParts[2]}.${channelParts[1]}`;
      }
    }
  }

  if (session.cardId) contextData.cardId = session.cardId;
  if (Object.keys(contextData).length > 0) {
    options.headers['x-at-context'] = JSON.stringify(contextData);
  }

  // replace sample data url with real data url when cardId is present
  if (isSampleData) {
    if (session.cardId) {
      isSampleData = false;
      url = `${config.API_URL}/api/gateway/card/${session.cardId}?channel=artifacts&includeCardDefinition=true`;
      options.headers.Authorization = `Bearer ${token}`;
    } else {
      console.info("fetchData: using sample data", url);
      options = {};
    }
  }

  if (!url.includes("://")) {
    url = config.API_URL + url;
    options.headers.Authorization = `Bearer ${token}`;
  }

  if (url.indexOf("https://api.adenin.com") > -1) {
    url = url.replace("https://api.adenin.com", config.API_URL);
  }

  // Add Bearer token for internal urls
  if (url.startsWith(config.API_URL)) {
    options.headers.Authorization = `Bearer ${token}`;
  }

  console.log("fetchData", url);
  let data;
  try {
    const response = await fetch(url, options);

    // Check for HTTP-level errors first
    if (!response.ok) {
      let emsg = `HTTP error ${url} status: ${response.status}`;
      ErrorReporter.error(emsg);
      const error = new Error(emsg);
      error.errorCode = response.status;
      throw error;
    }

    // Parse the response as JSON after confirming it's OK
    if (isSampleData) {
      const text = await response.text();
      data = JSON.parse(text, relativeDateReviver);
    } else {
      data = await response.json();
    }
  } catch (error) {
    // If error doesn't already include URL, add it to the message
    if (!error.message.includes(url)) {
      error.message = `${error.message} (URL: ${url})`;
    }
    throw error;
  }

  if (isSampleData) return data;

  // Check for API-level errors within the JSON response
  let ErrorCode = data.ErrorCode || data.Data?.ErrorCode || "unknown error code";
  let ErrorText = data.ErrorText || data.Data?.ErrorText || "unknown error";
  if (data.ErrorCode) {
    let emsg = `API error ${url} code: ${ErrorCode}, message: ${ErrorText}`;
    ErrorReporter.error(emsg);
    const error = new Error(ErrorText);
    error.errorCode = ErrorCode;
    error.errorText = ErrorText;
    error.url = url;
    error.data = data.Data;
    throw error;
  }

  // wrap plain array data in an object with an 'items' property
  let r = data.Data ? data.Data : data;
  if (Array.isArray(r)) r = { items: r };

  return r;
}